import compact from 'lodash/compact';
import get from 'lodash/get';
import { action, computed, makeObservable, observable, ObservableMap } from 'mobx';

import { GAME_PAGINATION_LIMIT } from '../../config/constants';
import type { SupportedLanguages } from '../../i18n/constants';
import { getUserLanguage } from '../../i18n/helpers';
import type { TranslatedData } from '../../i18n/types';
import { analytics } from '../../services/analytics';
import { getRequest, postRequest } from '../../services/api';
import type { ApiResponse } from '../../services/types';
import { CancellationError } from '../../utils/errors';
import { getPagination } from '../../utils/getPagination';
import type { Game } from '../game/types';
import { type RootStore } from '../RootStore';

type MasterGameSubCategoryId = number | string;

export type Category = {
  createdAt: string;
  name: TranslatedData<string>;
  thumbnailType: 'short' | 'long';
  subCategoryGames: Game[];
  masterGameSubCategoryId: MasterGameSubCategoryId;
  imageUrl: string;
  scImageUrl?: string;
  hasMoreGames: boolean;
  orderId: number;
  masterGameCategoryId: number;
  showInHamburgerMenu?: boolean;
  showInHeader?: boolean;
};

export interface SubCategoryGamesResponse {
  rows: Game[];
  count: number;
  hasMoreGames: boolean;
}

export const HOME_CATEGORY_ID: MasterGameSubCategoryId = -1;
export const SEARCH_CATEGORY_ID: MasterGameSubCategoryId = -2;

type LoadingKeys = 'categories' | 'pagination' | 'recommended-games' | 'search-games';

type CtgRegistry = Record<MasterGameSubCategoryId, Category>;
type GameLabelConfigs = Record<string, { name: string }>;

export class CategoriesStore {
  private readonly rootStore: RootStore;

  private lastCategoriesFetch?: {
    ts: number;
    controller: AbortController;
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  public lastLabelConfigsUpdateTs: number = Date.now();

  @observable
  public categoriesRegistry: CtgRegistry = {};

  @observable
  public gamesRegistry: Record<number, Game> = {};

  @observable
  public loading: ObservableMap<LoadingKeys, boolean> = observable.map([]);

  @observable
  public categoriesIds: Category['masterGameSubCategoryId'][] = [];

  @observable
  public currentId: Category['masterGameSubCategoryId'] = HOME_CATEGORY_ID;

  @observable
  public pageByCategory: { [key: Category['masterGameSubCategoryId']]: number } = {};

  @observable
  public headerHeight: number = 0;

  @observable
  public gamesHeroHeight: number = 0;

  public isScrollingProcess: boolean = false;

  @action
  public setIsScrollingProcess(isScrollingProcess: boolean) {
    this.isScrollingProcess = isScrollingProcess;
  }

  @action
  public setHeaderHeight(headerHeight: number) {
    this.headerHeight = headerHeight;
  }

  @action
  public setGamesHeroHeight(gamesHeroHeight: number) {
    this.gamesHeroHeight = gamesHeroHeight;
  }

  @computed
  public get currentCategory(): Category | undefined {
    return this.categoriesRegistry[this.currentId];
  }

  @action
  public setLoading(key: LoadingKeys, loading: boolean) {
    this.loading.set(key, loading);
  }

  @action
  private setCategories(ids: Category['masterGameSubCategoryId'][]) {
    this.categoriesIds = ids;
  }

  @action
  public registerGames(games: Game[]) {
    for (const game of games) {
      if (!this.gamesRegistry[game.masterCasinoGameId]) {
        this.gamesRegistry[game.masterCasinoGameId] = game;
      }
    }
  }

  @action
  public selectCategory(id: Category['masterGameSubCategoryId']) {
    this.currentId = id;
  }

  @action
  public setPageByCategory(pageByCategory: typeof this.pageByCategory) {
    this.pageByCategory = {
      ...this.pageByCategory,
      ...pageByCategory,
    };
  }

  public async getNextGames(id = this.currentId) {
    this.setLoading('pagination', true);
    try {
      const page = this.pageByCategory[id] ? this.pageByCategory[id] + 1 : 2;
      const response = await getRequest<ApiResponse<SubCategoryGamesResponse>>('user/casino-games', {
        limit: GAME_PAGINATION_LIMIT,
        page,
        subCategoryId: id,
      });
      this.handleGamesResponse(response, id);
      this.setPageByCategory({ [id]: page });
    } finally {
      this.setLoading('pagination', false);
    }
  }

  public async getNextSearchGames(loading: LoadingKeys, searchTerm = '', maxGamesLimit = 0) {
    this.setLoading(loading, true);
    try {
      const page = this.pageByCategory[this.currentId] ? this.pageByCategory[this.currentId] + 1 : 1;

      const pagination = getPagination(page, GAME_PAGINATION_LIMIT, maxGamesLimit);

      if (!pagination) {
        return;
      }

      const response = await getRequest<ApiResponse<SubCategoryGamesResponse>>('games/search', {
        limit: pagination.limit,
        page: pagination.page,
        text: searchTerm,
      });
      void analytics.sendEvent('game_search', {
        event_action: 'search',
        text: searchTerm,
        status: get(response, 'data.data.rows.length') ? 1 : 0,
      });
      this.handleGamesResponse(response);
      this.setPageByCategory({ [this.currentId]: page });
    } finally {
      this.setLoading(loading, false);
    }
  }

  @action
  setCategory(id: MasterGameSubCategoryId, category: Category) {
    this.categoriesRegistry = {
      ...this.categoriesRegistry,
      [id]: category,
    };
  }

  @action
  public updateRegistries(games: Game[], category: Category) {
    this.registerGames(games);
    this.setCategory(category.masterGameSubCategoryId, category);
  }

  @action
  public addGamesToCategory(categoryId: MasterGameSubCategoryId, games: Game[]) {
    const category = this.categoriesRegistry[categoryId];
    if (category) {
      category.subCategoryGames.push(...games);
      this.registerGames(games);
    }
  }

  private handleGamesResponse(response: ApiResponse<SubCategoryGamesResponse>, categoryId?: MasterGameSubCategoryId) {
    const { rows: games, hasMoreGames } = get(response, 'data.data', { rows: [], hasMoreGames: false });

    if (Array.isArray(games)) {
      let category: Category | undefined;

      if (categoryId) {
        category = this.categoriesRegistry[categoryId];
      } else {
        category = this.currentCategory;
      }

      if (category) {
        category.hasMoreGames = hasMoreGames;
        if (games.length > 0) {
          category.subCategoryGames.push(...games);
          this.updateRegistries(games, category);
        }
      }
    }
  }

  @action
  public resetCategories() {
    this.categoriesRegistry = {};
    this.gamesRegistry = {};
    this.categoriesIds = [];
  }

  @action
  private storeGameLabelConfigs(configs: GameLabelConfigs) {
    this.categoriesRegistry = Object.entries(this.categoriesRegistry).reduce<CtgRegistry>((registry, [ctgId, ctg]) => {
      registry[ctgId] = {
        ...ctg,
        subCategoryGames: ctg.subCategoryGames.map((game) => ({
          ...game,
          gameLabels: game.gameLabels.map((label) => {
            const updatedConfig = configs[label.id];

            return {
              ...label,
              name: updatedConfig?.name || label.name,
            };
          }),
        })),
      };

      return registry;
    }, {});

    this.lastLabelConfigsUpdateTs = Date.now();
  }

  public async updateGameLabelConfigs(lang?: SupportedLanguages) {
    const labelIds = Object.values(this.categoriesRegistry).reduce<string[]>((list, category) => {
      category.subCategoryGames.forEach((game) => {
        list.push(...game.gameLabels.map((label) => label.id));
      });

      return list;
    }, []);

    if (!labelIds.length) {
      return;
    }

    const response = await postRequest<ApiResponse<GameLabelConfigs>>('user/list-game-label-configs', {
      lang,
      labelIds,
    });

    if (Object.keys(response.data.data).length) {
      this.storeGameLabelConfigs(response.data.data);
    }
  }

  public async fetchCategories() {
    if (this.categoriesIds.length > 0) {
      this.resetCategories();
    }

    const ts = Date.now();
    const controller = new AbortController();

    if (this.lastCategoriesFetch?.controller) {
      this.lastCategoriesFetch.controller.abort();
    }

    this.lastCategoriesFetch = { ts, controller };

    this.setLoading('categories', true);
    try {
      const landingKeyParam = new URLSearchParams(window.location.search).get('landing');

      const response = await getRequest<ApiResponse<Category[]>>(
        'user/sub-category',
        {
          limit: GAME_PAGINATION_LIMIT, // Disabled on the backend and hardcoded as 20
          adaptive: true,
          landingKey: landingKeyParam,
          lang: getUserLanguage(),
        },
        controller.signal,
      );

      if (this.lastCategoriesFetch.ts !== ts) {
        return;
      }

      const categories = get(response, 'data.data', []);

      const categoriesIds = compact(categories).map((category) => {
        this.updateRegistries(category.subCategoryGames, category);
        return category.masterGameSubCategoryId;
      });

      this.setCategories(categoriesIds);
    } catch (error) {
      if (!(error instanceof CancellationError)) {
        throw error;
      }
    } finally {
      this.setLoading('categories', false);
    }
  }
}
